import React, { useMemo, useState } from 'react';
import { PlusSmIcon, TrashIcon } from '@heroicons/react/outline';
import ToDoAssigneeDropdown from './ToDoAssigneeDropdown';
import ToDoCategoryDropdown from './ToDoCategoryDropdown';
import ToDoForm from './ToDoForm';
import ToDoStatusDropdown from './ToDoStatusDropdown';

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return name || user?.email || user?.id || 'Unknown';
}

function getNextOrderForStatus(todos, statusId) {
  const inStatus = todos.filter((t) => t.status === statusId);
  const max = inStatus.reduce((acc, t) => Math.max(acc, Number.isFinite(t.order) ? t.order : 0), 0);
  return max + 1000;
}

function ToDoTableView({
  todos,
  todosLoading,
  statuses,
  categories,
  users,
  usersLoading,
  updateTodo,
  createTodo,
  deleteTodo,
}) {
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [sortKey, setSortKey] = useState('updatedAt');
  const [sortDir, setSortDir] = useState('desc');

  const statusById = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);
  const categoryById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? todos.filter((t) => {
          const name = (t.name || '').toLowerCase();
          const desc = (t.description || '').toLowerCase();
          return name.includes(q) || desc.includes(q);
        })
      : todos;

    const withComputed = base.map((t) => ({
      ...t,
      _statusLabel: statusById[t.status]?.label || t.status || '',
      _categoryLabel: categoryById[t.category]?.label || '',
      _assigneeLabel: displayName(userById[t.assignee]),
    }));

    const sorted = [...withComputed].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'name') return dir * String(a.name || '').localeCompare(String(b.name || ''));
      if (sortKey === 'status') return dir * String(a._statusLabel).localeCompare(String(b._statusLabel));
      if (sortKey === 'category') return dir * String(a._categoryLabel).localeCompare(String(b._categoryLabel));
      if (sortKey === 'assignee') return dir * String(a._assigneeLabel).localeCompare(String(b._assigneeLabel));
      if (sortKey === 'updatedAt') {
        const av = a.updatedAt?.toMillis?.() || 0;
        const bv = b.updatedAt?.toMillis?.() || 0;
        return dir * (av - bv);
      }
      return 0;
    });

    return sorted;
  }, [todos, query, sortKey, sortDir, statusById, categoryById, userById]);

  function toggleSort(nextKey) {
    if (sortKey === nextKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === 'name' ? 'asc' : 'desc');
  }

  return (
    <div className="mt-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <input
            className="border rounded-lg px-3 py-2 text-sm w-full md:w-[22rem] bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
            placeholder="Search name / description…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            className="px-3 py-2 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 transition flex items-center gap-2"
            onClick={() => setCreateVisible((p) => !p)}
            type="button"
          >
            <PlusSmIcon className="w-5 h-5" />
            New
          </button>
        </div>
      </div>

      {createVisible && (
        <div className="mt-3">
          <ToDoForm
            mode="create"
            statuses={statuses}
            categories={categories}
            users={users}
            usersLoading={usersLoading}
            onSave={async (data) => {
              await createTodo(data);
              setCreateVisible(false);
            }}
            onCancel={() => setCreateVisible(false)}
          />
        </div>
      )}

      <div className="mt-4 bg-white shadow-basic rounded-lg border overflow-visible">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-3 border-b">
                  <button type="button" className="hover:underline" onClick={() => toggleSort('name')}>
                    Name
                  </button>
                </th>
                <th className="text-left p-3 border-b w-[14rem]">
                  <button type="button" className="hover:underline" onClick={() => toggleSort('category')}>
                    Category
                  </button>
                </th>
                <th className="text-left p-3 border-b w-[16rem]">
                  <button type="button" className="hover:underline" onClick={() => toggleSort('assignee')}>
                    Assignee
                  </button>
                </th>
                <th className="text-left p-3 border-b w-[14rem]">
                  <button type="button" className="hover:underline" onClick={() => toggleSort('status')}>
                    Status
                  </button>
                </th>
                <th className="text-right p-3 border-b w-[7rem]">
                  <button type="button" className="hover:underline" onClick={() => toggleSort('updatedAt')}>
                    Updated
                  </button>
                </th>
                <th className="text-right p-3 border-b w-[4rem]"> </th>
              </tr>
            </thead>
            <tbody>
              {todosLoading ? (
                <tr>
                  <td className="p-3 text-neutral-500" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td className="p-3 text-neutral-500" colSpan={6}>
                    No tasks
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <React.Fragment key={t.id}>
                    <tr
                      className="odd:bg-white even:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedId((prev) => (prev === t.id ? null : t.id))}
                    >
                      <td className="p-3 border-b align-top">
                        <div className="font-medium">{t.name || '—'}</div>
                        {t.description ? (
                          <div className="text-xs text-neutral-500 mt-0.5 max-w-[52rem] truncate">
                            {t.description}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-3 border-b align-top" onClick={(e) => e.stopPropagation()}>
                        <ToDoCategoryDropdown
                          value={t.category || ''}
                          categories={categories}
                          onChange={(next) => updateTodo(t.id, { category: next })}
                        />
                      </td>
                      <td className="p-3 border-b align-top" onClick={(e) => e.stopPropagation()}>
                        <ToDoAssigneeDropdown
                          value={t.assignee ?? null}
                          users={users}
                          loading={usersLoading}
                          onChange={(next) => updateTodo(t.id, { assignee: next })}
                        />
                      </td>
                      <td className="p-3 border-b align-top" onClick={(e) => e.stopPropagation()}>
                        <ToDoStatusDropdown
                          value={t.status}
                          statuses={statuses}
                          onChange={(nextStatus) =>
                            updateTodo(t.id, {
                              status: nextStatus,
                              order: getNextOrderForStatus(todos, nextStatus),
                            })
                          }
                        />
                      </td>
                      <td className="p-3 border-b align-top text-right text-xs text-neutral-500">
                        {t.updatedAt?.toDate?.()?.toLocaleString?.() || '—'}
                      </td>
                      <td className="p-3 border-b align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                          title="Delete"
                          type="button"
                          onClick={async () => {
                            const ok = window.confirm(`Delete "${t.name || 'task'}"?`);
                            if (!ok) return;
                            await deleteTodo(t.id);
                            setExpandedId((prev) => (prev === t.id ? null : prev));
                          }}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                    {expandedId === t.id && (
                      <tr className="bg-white">
                        <td className="p-3 border-b" colSpan={6} onClick={(e) => e.stopPropagation()}>
                          <ToDoForm
                            mode="edit"
                            initialTodo={t}
                            statuses={statuses}
                            categories={categories}
                            users={users}
                            usersLoading={usersLoading}
                            onSave={async (data) => {
                              await updateTodo(t.id, data);
                              setExpandedId(null);
                            }}
                            onCancel={() => setExpandedId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ToDoTableView;
